// src/features/settings/settingsSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

// État initial
const initialState = {
  settings: {
    siteName: 'Hajj Packages',
    contactEmail: 'contact@hajj-packages.com',
    contactPhone: '+33 1 23 45 67 89',
    departureCities: ['Paris', 'Lyon', 'Marseille', 'Lille', 'Toulouse', 'Nice', 'Bordeaux'],
    maxPackagesPerPage: 12,
    maxCompaniesPerPage: 10,
  },
  isLoading: false,
  error: null,
};

// Action asynchrone pour récupérer les paramètres
export const fetchSettings = createAsyncThunk(
  'settings/fetchSettings',
  async (_, { rejectWithValue }) => {
    try {
      // Dans une version complète, cette route API existerait
      const response = await api.get('/admin/settings');
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Erreur lors de la récupération des paramètres'
      );
    }
  }
);

// Action asynchrone pour mettre à jour les paramètres
export const updateSettings = createAsyncThunk(
  'settings/updateSettings',
  async (settingsData, { rejectWithValue }) => {
    try {
      // Dans une version complète, cette route API existerait
      const response = await api.put('/admin/settings', settingsData);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Erreur lors de la mise à jour des paramètres'
      );
    }
  }
);

// Action asynchrone pour ajouter une ville de départ
export const addDepartureCity = createAsyncThunk(
  'settings/addDepartureCity',
  async (city, { rejectWithValue, getState }) => {
    try {
      // Dans une version complète, cette route API existerait
      const response = await api.post('/admin/settings/departure-cities', { city });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Erreur lors de l\'ajout de la ville'
      );
    }
  }
);

// Action asynchrone pour supprimer une ville de départ
export const deleteDepartureCity = createAsyncThunk(
  'settings/deleteDepartureCity',
  async (city, { rejectWithValue }) => {
    try {
      // Dans une version complète, cette route API existerait
      await api.delete(`/admin/settings/departure-cities/${city}`);
      return city;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Erreur lors de la suppression de la ville'
      );
    }
  }
);

// Slice Redux pour les paramètres
const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    resetSettingsError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Gestion de fetchSettings
      .addCase(fetchSettings.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSettings.fulfilled, (state, action) => {
        state.isLoading = false;
        state.settings = action.payload;
      })
      .addCase(fetchSettings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // Gestion de updateSettings
      .addCase(updateSettings.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateSettings.fulfilled, (state, action) => {
        state.isLoading = false;
        state.settings = action.payload;
      })
      .addCase(updateSettings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // Gestion de addDepartureCity
      .addCase(addDepartureCity.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addDepartureCity.fulfilled, (state, action) => {
        state.isLoading = false;
        if (!state.settings.departureCities.includes(action.meta.arg)) {
          state.settings.departureCities.push(action.meta.arg);
        }
      })
      .addCase(addDepartureCity.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // Gestion de deleteDepartureCity
      .addCase(deleteDepartureCity.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteDepartureCity.fulfilled, (state, action) => {
        state.isLoading = false;
        state.settings.departureCities = state.settings.departureCities.filter(
          (city) => city !== action.meta.arg
        );
      })
      .addCase(deleteDepartureCity.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

// Export des actions
export const { resetSettingsError } = settingsSlice.actions;

// Sélecteurs
export const selectSettings = (state) => state.settings.settings;
export const selectDepartureCities = (state) => state.settings.settings.departureCities;
export const selectSettingsLoading = (state) => state.settings.isLoading;
export const selectSettingsError = (state) => state.settings.error;

// Export du reducer
export default settingsSlice.reducer;